import { useMemo } from 'react';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import { SearchFriendResultDescription } from '@/components/search/search-friend-result-item';
import { HighlightKeyword } from '@/components/search/search-list-item';
import type { ForwardListItem } from '@/hooks/contacts/use-forward-list';
import cn from '@/utils/merge-class-names';

type Props = {
  session: ForwardListItem;
  isSelected: boolean;
  onSelectSession: (forwardSession: ForwardListItem, selected: boolean) => void;
  className?: string;
  showCheckBox?: boolean;
};

export default function ForwardSessionListItem({
  session,
  isSelected,
  onSelectSession,
  showCheckBox = true,
  className,
}: Props) {
  const titleHighlightElement = useMemo(() => {
    if (session.type === 'user' && session.remarkName) {
      return session.FuzzySearch?.highlightRanges?.find((r) => r.key === 'RemarkName');
    }
    return session.FuzzySearch?.highlightRanges?.find(
      (r) => r.key && ['FriendNickName', 'NickName', 'GroupName'].includes(r.key),
    );
  }, [session]);

  return (
    <button
      type='button'
      className={cn('flex w-full py-2 px-5 items-stretch gap-4 rounded-2xl transition-colors min-h-12', className)}
      onClick={() => onSelectSession(session, !isSelected)}>
      {session.imageURL && (
        <div className='flex flex-col justify-center'>
          <AvatarWithStatus
            isOnline={false}
            className='size-12'
            size={[48, 48]}
            imageUrl={session.imageURL}
            label={session.name}
          />
        </div>
      )}

      <div className='text-start flex min-w-0 flex-auto flex-col items-stretch justify-center gap-1 overflow-hidden'>
        <h5 className='line-clamp-1 break-all text-h2 desktop:text-sm'>
          <HighlightKeyword
            highlight={titleHighlightElement?.indices}
            text={session.name}
          />
        </h5>
        {session.type === 'user' && session.remarkName && (
          <p className={cn('truncate text-xs text-base-content/50')}>
            <SearchFriendResultDescription
              id={session.userId}
              fuzzySearch={session.FuzzySearch}
            />
          </p>
        )}
      </div>

      {showCheckBox && (
        <div className='flex flex-1 items-center justify-end'>
          <input
            type='checkbox'
            name={`select-${session.key}`}
            className='checkbox rounded-full checked:checkbox-primary'
            readOnly
            checked={isSelected}
          />
        </div>
      )}
    </button>
  );
}
